import React, {useCallback} from 'react';
import {APIActionStatus, Form, formsStore} from '../../../../stores/Form';
import {observer} from 'mobx-react';
import {texts} from './texts';
import {HeaderHelpButton} from '../../../../_shared/Help/components/FormHelpUI';
import {
    Button,
    Flex,
    Icon,
    noop,
    PriorityIcon,
    Title
} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {FormView} from '../FormView';
import {SubForms} from '../SubForms/SubForms';
import {isRTL} from '../../../../Localization';
import {useEventListener} from '../../../../hooks/useEventListener';
import {keys} from '../../../../constants/KeyCodes';
import {isNavigationLocked} from '../../utils/isNavigationLocked';
import {createDataTestId} from '../../../../utils/dataTestId';
import {Actions} from '../FormToolbar/parts/Actions/Actions';
import {isCurrentRecordEmpty} from '../Table/utils/isCurrentRecordEmpty';
import {deleteRecord} from '../../actions/delete';
import {undoAction} from '../../actions/undo';
import {showFormHelp} from '../../../../_shared/Help/actions/helpActions';
import {formF12handler} from '../../utils/formShortcuts';
import styles from './styles.module.scss';
import {callLinkedActions} from '../../actions/screen';

type Props = {
    currentForm: Form;
    runSubForm: (subFormName: string, parentLevel: number) => Promise<void>;
    closeRecordDialog: () => void;
    isApiLock: () => boolean;
    isFormActive: boolean;
    disabledSubformList: boolean;
};

export const FocusViewDialog = observer((props: Props) => {
    const {
        currentForm,
        runSubForm,
        closeRecordDialog,
        isApiLock,
        disabledSubformList,
        isFormActive
    } = props;

    const onBack = useCallback(async () => {
        const currentScreen = formsStore.currentScreen;
        await callLinkedActions(
            async () => await currentScreen.handleFormLevel(0),
            async () => {
                closeRecordDialog();
                return APIActionStatus.passed;
            }
        );
    }, [currentForm, closeRecordDialog]);

    useEventListener('keydown', (e: KeyboardEvent) => {
        if (!isNavigationLocked(currentForm)) {
            const ctrlKey = e.ctrlKey;

            if (e.key === keys.ESC) {
                onBack();
            } else if (e.key === keys.F1 && ctrlKey) {
                showFormHelp();
            } else if (e.key === keys.F12) {
                formF12handler(e, currentForm);
            }
        }
    });

    if (!currentForm) return null;

    const getTitle = () => {
        const isNewRecord = currentForm?.currentRecord?.isNew;
        const docName = currentForm.form.DocName || currentForm.form.Title;
        if (isNewRecord) {
            return `${texts.addLabel} ${docName}`;
        }

        const keyColumn =
            currentForm?.columns.find(col => col.Key && col.Mandatory) || currentForm?.columns[0];
        return `${docName}: ${currentForm.currentRecord.getField(keyColumn.id).currentValue}`;
    };

    return (
        <div className={styles.component}>
            <div className={styles.inner} {...createDataTestId('screen-toolbar', false)}>
                <header className={styles.dialogHeader}>
                    <Button
                        skin="primary"
                        onClick={onBack}
                        prefixIcon={isRTL() ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft}
                    >
                        {texts.backTo}
                    </Button>

                    <div className={styles.toolbar}>
                        <HeaderHelpButton tooltipPosition="bottom-end" />

                        <div className={styles.divider} />
                        <Icon icon={PriorityIcon.match} onClick={closeRecordDialog} />
                    </div>
                </header>

                <div
                    className={styles.formHeader}
                    {...createDataTestId('form-header-with-add', false)}
                >
                    <Title skin="h3" textColor="primary-500" componentClasses={styles.title}>
                        {getTitle()}
                    </Title>
                    <Flex justifyContent="flex-end">
                        <Actions
                            actions={currentForm?.actions || []}
                            disabled={currentForm?.currentRecord?.isEmpty()}
                        />
                        <div className={cx(styles.divider, styles.marginXmd)} />
                        <Flex inline style={{gap: 8}}>
                            <Icon icon={PriorityIcon.refresh} onClick={undoAction} />
                            <Icon
                                icon={PriorityIcon.delete}
                                disabled={isCurrentRecordEmpty(currentForm) || !isFormActive}
                                onClick={() => deleteRecord(currentForm)}
                            />
                        </Flex>
                    </Flex>
                </div>

                <div className={styles.mainForm}>
                    <FormView
                        currentForm={currentForm}
                        isActiveForm={formsStore.currentScreen?.activeLevel === currentForm?.level}
                    />
                </div>

                <div className={styles.subform}>
                    <SubForms
                        parentLevel={currentForm.level}
                        disabledSubformList={disabledSubformList}
                        isApiLock={isApiLock}
                        subForm={currentForm.activeTabSubForm}
                        subFormsList={currentForm.subFormsList}
                        runSubForm={runSubForm}
                        openSelectListModal={noop}
                    />
                </div>
            </div>
        </div>
    );
});
